// SignupEmailCollect.jsx
//
// Phase 3c.2b — the email-collection step of the OAuth signup flow.
// Users land here after completing Facebook OAuth (other platforms in
// Phase 3d/3e/4 will use the same page). The OAuth callback inserted a
// pending_oauth_signups row with the verified FB Page + BusinessSeed;
// this page just needs the user's email so we can send a magic-link to
// confirm they own the address before creating the account.
//
// Flow:
//   1. Read oauth_token from URL query string.
//   2. User enters email → POST /api/auth/signup/oauth/email-collect.
//   3. On success, swap UI to "Check your email" confirmation.
//   4. User clicks magic-link in email → server creates account →
//      redirects them to /create-password to set a password.
//
// Standalone page (not embedded in the modal) so it survives a refresh
// — the oauth_token in the URL is the entire session state.

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const BRAND = '#00aef7';

export default function SignupEmailCollect() {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [oauthToken, setOauthToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sentToEmail, setSentToEmail] = useState('');  // truthy = success view

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('oauth_token');
    if (!t) {
      setError("This page is for finishing a Facebook sign-up. We don't see your sign-up session — please start again from the homepage.");
      return;
    }
    setOauthToken(t);
  }, [location.search]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    try {
      const resp = await fetch('/api/auth/signup/oauth/email-collect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oauth_token: oauthToken, email: cleanEmail }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.ok) {
        throw new Error(data.error || 'Could not send verification email.');
      }
      setSentToEmail(cleanEmail);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  // Success view — magic-link was sent.
  if (sentToEmail) {
    return (
      <div className="max-w-md mx-auto mt-16 px-6 py-8 bg-white rounded-xl shadow-md">
        <h1 className="text-2xl font-bold text-center mb-4" style={{ color: BRAND }}>
          Check your email
        </h1>
        <p className="text-gray-700 text-center mb-2">
          We just sent a verification link to:
        </p>
        <p className="text-gray-900 font-medium text-center mb-6">
          {sentToEmail}
        </p>
        <p className="text-sm text-gray-600 text-center mb-4">
          Click the link to verify your email and finish setting up your account. The link expires in 30 minutes.
        </p>
        <p className="text-xs text-gray-400 text-center">
          Didn&apos;t get the email? Check your spam folder, or{' '}
          <button
            type="button"
            onClick={() => { setSentToEmail(''); setEmail(''); }}
            className="underline hover:text-gray-600"
          >
            try a different address
          </button>
          .
        </p>
      </div>
    );
  }

  // Entry view — collect email.
  return (
    <div className="max-w-md mx-auto mt-16 px-6 py-8 bg-white rounded-xl shadow-md">
      <h1 className="text-2xl font-bold text-center mb-2" style={{ color: BRAND }}>
        Almost done!
      </h1>
      <p className="text-sm text-gray-700 text-center mb-6">
        Facebook verified your Business Page. We just need an email address for login and account communication. We&apos;ll send you a verification link to confirm it.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="email"
          placeholder="your-email@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={!oauthToken || isSubmitting}
          className="w-full p-2 border rounded focus:ring-2 focus:ring-brand focus:border-transparent"
          autoFocus
          required
        />
        <button
          type="submit"
          disabled={!oauthToken || isSubmitting}
          className="w-full bg-brand text-white p-2 rounded hover:bg-brand-dark transition-colors disabled:opacity-50"
        >
          {isSubmitting ? 'Sending…' : 'Send me a verification link'}
        </button>
      </form>

      <p className="text-xs text-gray-400 text-center mt-6">
        Any email is fine — your business identity was verified via Facebook. This email is just for login.
      </p>
    </div>
  );
}
